import {
  CombinedSelectOptions,
  ContentGenre,
  Country, Currency,
  DspAccountsContainer,
  featureToggleKeys, FeatureToggles, IabCategoryParent, OperationsOwner, OptionsLists,
  PublisherEdit,
  PublisherTrafficSource,
  ResponseFromServer,
  SamOwner,
  SharedStoreAssetConfig,
  SharedStoreAssetKey, SupplySource, Timezone, TrafficSource
} from '../_models/models';
import { BehaviorSubject, Observable } from 'rxjs';
import _, { sortBy } from 'lodash';
import { Common } from '../_common/common.helper';

export type SharedStoreResponseType = FeatureToggles | OptionsLists | CombinedSelectOptions |
DspAccountsContainer | Country | OperationsOwner | SamOwner | Timezone | Currency | TrafficSource |
PublisherTrafficSource | IabCategoryParent | SupplySource | PublisherEdit;

const timezonesResponseHandler = (res: ResponseFromServer<Timezone[]>, config: SharedStoreAssetConfig<Timezone>) => {
  res.data = sortBy(res.data, ['gmtOffset']);
  return res.data.map((tz: Timezone) => Object.assign({ ...tz }, { dropdownLabel: Common.prototype.timezoneToDropdownLabel(tz) }));
};


const iabCategoriesResponseHandler = (res: ResponseFromServer<IabCategoryParent[]>, config: SharedStoreAssetConfig<IabCategoryParent>) => {
  res.data = _.orderBy(res.data, ['title']);
  res.data.forEach(parent => {
    parent.children = _.orderBy(parent.children, ['title']);
  });
  return res.data;
};

export const sharedAssetsConfigs: Record<SharedStoreAssetKey, SharedStoreAssetConfig<SharedStoreResponseType>> = {
  featureToggles: {
    key: 'featureToggles',
    isLoading: false,
    response$: null,
    queueSubject: new BehaviorSubject<Observable<FeatureToggles>>(null),
    responseIntegrityTest: (res: FeatureToggles) => Object.keys(res).length === [...featureToggleKeys].length,
  },
  optionsLists: {
    key: 'optionsLists',
    isLoading: false,
    response$: null,
    queueSubject: new BehaviorSubject<Observable<OptionsLists>>(null),
    responseIntegrityTest: (res: OptionsLists) => Object.keys(res).length > 0,
  },
  combinedSelectOptions: {
    key: 'combinedSelectOptions',
    isLoading: false,
    response$: null,
    queueSubject: new BehaviorSubject<Observable<ResponseFromServer<CombinedSelectOptions>>>(null),
    responseIntegrityTest: (res: ResponseFromServer<CombinedSelectOptions>) => !!res.data,
  },
  countries: {
    key: 'countries',
    isLoading: false,
    response$: null,
    queueSubject: new BehaviorSubject<Observable<ResponseFromServer<Country[]>>>(null),
    responseIntegrityTest: (res: ResponseFromServer<Country[]>) => res.data.length > 0,
  },
  opsOwners: {
    key: 'opsOwners',
    isLoading: false,
    response$: null,
    queueSubject: new BehaviorSubject<Observable<ResponseFromServer<OperationsOwner[]>>>(null),
    responseIntegrityTest: (res: ResponseFromServer<OperationsOwner[]>) => res.data.length > 0,
  },
  samOwners: {
    key: 'samOwners',
    isLoading: false,
    response$: null,
    queueSubject: new BehaviorSubject<Observable<ResponseFromServer<SamOwner[]>>>(null),
    responseIntegrityTest: (res: ResponseFromServer<SamOwner[]>) => !!res?.data,
  },
  bizdevOwners: {
    key: 'bizdevOwners',
    isLoading: false,
    response$: null,
    queueSubject: new BehaviorSubject<Observable<ResponseFromServer<OperationsOwner[]>>>(null),
    responseIntegrityTest: (res: ResponseFromServer<OperationsOwner[]>) => res.data.length > 0,
  },
  timezones: {
    key: 'timezones',
    responseHandler: timezonesResponseHandler,
    isLoading: false,
    response$: null,
    queueSubject: new BehaviorSubject<Observable<ResponseFromServer<Timezone[]>>>(null),
    responseIntegrityTest: (res: ResponseFromServer<Timezone[]>) => res.data.length > 0,
  },
  currencies: {
    key: 'currencies',
    responseHandler: (res: ResponseFromServer<Currency[]>) => res.data,
    isLoading: false,
    response$: null,
    queueSubject: new BehaviorSubject<Observable<ResponseFromServer<Currency[]>>>(null),
    responseIntegrityTest: (res: ResponseFromServer<Currency[]>) => res.data.length > 0,
  },
  trafficSources: {
    key: 'trafficSources',
    isLoading: false,
    response$: null,
    queueSubject: new BehaviorSubject<Observable<ResponseFromServer<TrafficSource[]>>>(null),
    responseIntegrityTest: (res: ResponseFromServer<TrafficSource[]>) => res.data.length > 0,
  },
  trafficSourcesByPublisherId: {
    key: 'trafficSourcesByPublisherId',
    responseHandler: (res: PublisherTrafficSource[]) => ({ data: res } as ResponseFromServer<PublisherTrafficSource[]>),
    isLoading: false,
    response$: null,
    queueSubject: new BehaviorSubject<Observable<ResponseFromServer<PublisherTrafficSource[]>>>(null),
    responseIntegrityTest: (res: PublisherTrafficSource[]) => Array.isArray(res),
  },
  ssps: {
    key: 'ssps',
    isLoading: false,
    response$: null,
    queueSubject: new BehaviorSubject<Observable<ResponseFromServer<SupplySource[]>>>(null),
    responseIntegrityTest: (res: SupplySource[]) => res.length > 0,
  },
  iabCategories: {
    key: 'iabCategories',
    responseHandler: iabCategoriesResponseHandler,
    isLoading: false,
    response$: null,
    queueSubject: new BehaviorSubject<Observable<ResponseFromServer<IabCategoryParent[]>>>(null),
    responseIntegrityTest: (res: ResponseFromServer<Currency[]>) => res.data.length > 0,
  },
  contentAttributes: {
    key: 'contentAttributes',
    isLoading: false,
    response$: null,
    queueSubject: new BehaviorSubject<Observable<ResponseFromServer<ContentGenre[]>>>(null),
    responseIntegrityTest: (res: ResponseFromServer<ContentGenre[]>) => res.data.length > 0,
  },
  dspAccountsGrouped: {
    key: 'dspAccountsGrouped',
    responseHandler: (res: DspAccountsContainer) => ({ data: res } as ResponseFromServer<DspAccountsContainer>),
    isLoading: false,
    response$: null,
    queueSubject: new BehaviorSubject<Observable<ResponseFromServer<DspAccountsContainer>>>(null),
    responseIntegrityTest: (res: DspAccountsContainer) =>
      res?.activeDspAccountList?.length > 0 || res?.inactiveDspAccountList?.length > 0,
  },
  publisherForLayout: {
    key: 'publisherForLayout',
    isLoading: false,
    response$: null,
    queueSubject: new BehaviorSubject<Observable<PublisherEdit>>(null),
    responseIntegrityTest: (res: PublisherEdit) => res.publisherId > 0
  }
};
